import {
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_API_KEY,
  ALGOLIA_INDICES,
  ALGOLIA_FILTERS,
} from '../search/algolia/client';
import { buildAlgoliaSearchAttribution } from '../search/algolia/attribution';
import { renderProductCard, initSwatchDelegation } from './product-card';

const MODEL_MAP = {
  looking_similar: 'looking-similar',
  related_products: 'related-products',
  frequently_bought_together: 'bought-together',
  trending_items: 'trending-items',
};

const MODELS_REQUIRING_OBJECT_ID = new Set([
  'looking_similar',
  'related_products',
  'frequently_bought_together',
]);

const LOCAL_MODELS = new Set(['recently_viewed']);

const RECOMMEND_HOSTS = [
  `${ALGOLIA_APP_ID}-dsn.algolia.net`,
  `${ALGOLIA_APP_ID}.algolia.net`,
];

function normalizeRecentlyViewedProduct(product) {
  if (!product || !product.handle) return null;

  const toDisplayPrice = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount / 100 : 0;
  };

  const decodeHtmlEntities = (value) => {
    if (typeof value !== 'string') return '';

    return value
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  };

  const getSwatchImage = (colorValue) => {
    if (!colorValue || !Array.isArray(product.images)) return '';

    const imagePattern = new RegExp(`^\\s*${String(colorValue).toLowerCase()}\\s*swatch\\s*$`);
    const matchedImage = product.images.find((image) => {
      const alt = decodeHtmlEntities(String(image?.alt || '').toLowerCase());
      return alt && imagePattern.test(alt);
    });

    return matchedImage?.src || '';
  };

  const colorOption = Array.isArray(product.options)
    ? product.options.find((option) => /colou?r/i.test(option.name || ''))
    : null;
  const colorOptionIndex = colorOption
    ? Array.isArray(product.options)
      ? product.options.findIndex((option) => option?.name === colorOption.name)
      : -1
    : -1;

  const swatches = colorOptionIndex >= 0 && Array.isArray(product.variants)
    ? product.variants.reduce((acc, variant) => {
      const colorValue = variant[`option${colorOptionIndex + 1}`];
      const image = variant?.featured_image?.src || variant?.featured_image || product?.featured_image?.src || product?.featured_image || '';

      if (!colorValue) return acc;

      const existing = acc.find((swatch) => swatch.color === colorValue);
      if (existing) {
        if (!existing.available && variant.available) {
          existing.available = true;
          existing.price = toDisplayPrice(variant.price);
          existing.compare_at_price = toDisplayPrice(variant.compare_at_price || product.compare_at_price || 0);
          existing.image = image;
          existing.hover_image = image;
          existing.variant_id = variant.id;
        }
        return acc;
      }

      acc.push({
        color: colorValue,
        available: Boolean(variant.available),
        price: toDisplayPrice(variant.price),
        compare_at_price: toDisplayPrice(variant.compare_at_price || product.compare_at_price || 0),
        image,
        hover_image: image,
        swatch_image: getSwatchImage(colorValue),
        variant_id: variant.id,
      });

      return acc;
    }, [])
    : [];

  return {
    objectID: String(product.first_variant?.id || product.id || product.handle),
    id: product.id,
    handle: product.handle,
    url: `/products/${product.handle}`,
    title: product.title,
    price: toDisplayPrice(product.price),
    compare_at_price: toDisplayPrice(product.compare_at_price || 0),
    tags: product.tags || [],
    featured_image: product.featured_image?.src || product.featured_image || '',
    image: product.featured_image?.src || product.featured_image || product.images?.[0]?.src || '',
    hover_image: product.images?.[1]?.src || product.images?.[0]?.src || product.featured_image?.src || product.featured_image || '',
    swatches,
  };
}

async function fetchRecentlyViewedRecommendations(handles, maxProducts) {
  const uniqueHandles = [...new Set(handles.filter(Boolean))].slice(0, maxProducts);
  if (uniqueHandles.length === 0) return [];

  const results = await Promise.allSettled(
    uniqueHandles.map(async (handle) => {
      const response = await fetch(`/products/${handle}?view=json`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const product = await response.json();
      return normalizeRecentlyViewedProduct(product);
    })
  );

  return results
    .filter((result) => result.status === 'fulfilled' && result.value)
    .map((result) => result.value);
}

async function callRecommendAPI(requestBody) {
  let lastError = null;

  for (const host of RECOMMEND_HOSTS) {
    try {
      const response = await fetch(
        `https://${host}/1/indexes/*/recommendations`,
        {
          method: 'POST',
          headers: {
            'X-Algolia-Application-Id': ALGOLIA_APP_ID,
            'X-Algolia-API-Key': ALGOLIA_SEARCH_API_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[RecommendationReels] API HTTP error:', response.status, errorText);
        lastError = new Error(`HTTP ${response.status}: ${errorText}`);
        continue;
      }

      return await response.json();
    } catch (err) {
      console.error('[RecommendationReels] Network error with host', host, ':', err);
      lastError = err;
    }
  }

  throw lastError || new Error('All Algolia hosts failed');
}

function buildRequest({ modelType, objectID, maxProducts }) {
  const model = MODEL_MAP[modelType];
  if (!model) return null;

  const request = {
    indexName: ALGOLIA_INDICES.products,
    model,
    threshold: 0,
    maxRecommendations: maxProducts,
    queryParameters: {
      clickAnalytics: true,
      filters: ALGOLIA_FILTERS.hiddenProductsExpression,
    },
  };

  if (modelType !== 'trending_items' && objectID) {
    request.objectID = String(objectID);
  }

  return request;
}

async function fetchAllRecommendations(panelConfigs, objectID) {
  const requests = panelConfigs
    .map(({ modelType, maxProducts }) => buildRequest({ modelType, objectID, maxProducts }))
    .filter(Boolean);

  if (requests.length === 0) return [];

  console.log('[RecommendationReels] Batched API request:', requests.length, 'model(s)');

  let results = [];
  try {
    const data = await callRecommendAPI({ requests });
    results = data.results || [];
  } catch (err) {
    console.error('[RecommendationReels] Batch API error:', err);
    return panelConfigs.map(() => ({ hits: [], queryID: null }));
  }

  const panelResults = results.map((r) => ({
    hits: (r && r.hits) || [],
    queryID: (r && r.queryID) || null,
  }));

  const fallbackIndices = [];
  panelConfigs.forEach(({ modelType }, i) => {
    if (panelResults[i].hits.length === 0 && modelType !== 'looking_similar' && objectID) {
      fallbackIndices.push(i);
    }
  });

  if (fallbackIndices.length > 0) {
    const fallbackRequests = fallbackIndices
      .map((i) => buildRequest({ modelType: 'looking_similar', objectID, maxProducts: panelConfigs[i].maxProducts }))
      .filter(Boolean);

    if (fallbackRequests.length > 0) {
      console.log('[RecommendationReels] Fetching', fallbackRequests.length, 'looking_similar fallback(s)');
      try {
        const fallbackData = await callRecommendAPI({ requests: fallbackRequests });
        const fallbackResults = fallbackData.results || [];
        fallbackIndices.forEach((originalIdx, fallbackIdx) => {
          const r = fallbackResults[fallbackIdx];
          const fallbackHits = (r && r.hits) || [];
          if (fallbackHits.length > 0) {
            panelResults[originalIdx] = {
              hits: fallbackHits,
              queryID: (r && r.queryID) || null,
            };
          }
        });
      } catch (err) {
        console.error('[RecommendationReels] Fallback batch error:', err);
      }
    }
  }

  return panelResults;
}

function waitForCarouselFn(maxRetries = 50) {
  return new Promise((resolve) => {
    if (window.carouselFn) {
      resolve(true);
      return;
    }

    let retries = 0;
    const interval = setInterval(() => {
      retries++;
      if (window.carouselFn || retries >= maxRetries) {
        clearInterval(interval);
        resolve(Boolean(window.carouselFn));
      }
    }, 100);
  });
}

async function renderCarousel(panelEl, hits, objectID, { queryID, indexName } = {}) {
  const filtered = objectID
    ? hits.filter((hit) => String(hit.objectID) !== String(objectID))
    : hits;

  console.log('[RecommendationReels] renderCarousel — total hits:', hits.length, '| after filtering current product:', filtered.length);

  if (filtered.length === 0) {
    console.warn('[RecommendationReels] No products to render after filtering');
    return false;
  }

  panelEl.innerHTML = `
    <div class="rec-carousel" data-insights-index="${indexName || ''}">
      <div class="swiper__row">
        ${filtered.map((hit, i) => `<div class="swiper__col">${renderProductCard(hit, { queryID, position: i + 1, indexName })}</div>`).join('')}
      </div>
      <button class="rec-carousel__nav rec-carousel__prev" aria-label="Previous">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M7.5 1.5L3 6L7.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button class="rec-carousel__nav rec-carousel__next" aria-label="Next">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M4.5 1.5L9 6L4.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  `;

  const ready = await waitForCarouselFn();
  console.log('[RecommendationReels] window.carouselFn available:', ready);
  if (!ready) {
    console.warn('[RecommendationReels] Swiper carouselFn never became available — carousel will not be interactive');
    return true;
  }

  const carouselEl = panelEl.querySelector('.rec-carousel');
  const nextBtn = panelEl.querySelector('.rec-carousel__next');
  const prevBtn = panelEl.querySelector('.rec-carousel__prev');

  window.carouselFn(carouselEl, {
    slidesPerView: 1,
    spaceBetween: 16,
    slideClass: 'swiper__col',
    wrapperClass: 'swiper__row',
    navigation: { nextEl: nextBtn, prevEl: prevBtn },
    breakpoints: {
      768: { slidesPerView: 2.6 },
      1024: { slidesPerView: 4, spaceBetween: 32 },
    },
    a11y: { enabled: true },
    threshold: 10,
    touchRatio: 0.5,
    preventClicks: false,
    preventClicksPropagation: false,
    touchReleaseOnEdges: true,
    watchSlidesProgress: true,
  });

  return true;
}

function initTabs(sectionEl) {
  const tabsContainer = sectionEl.querySelector('.recommendation-reels__tabs');
  if (!tabsContainer) return;

  tabsContainer.addEventListener('click', (e) => {
    const clickedTab = e.target.closest('.recommendation-reels__tab');
    if (!clickedTab) return;

    const index = clickedTab.dataset.tabIndex;
    const tabs = sectionEl.querySelectorAll('.recommendation-reels__tab');
    const panels = sectionEl.querySelectorAll('.recommendation-reels__panel');

    tabs.forEach((t) => t.classList.remove('is-active'));
    clickedTab.classList.add('is-active');

    panels.forEach((panel) => {
      panel.style.display = panel.dataset.panelIndex === index ? '' : 'none';
    });
  });
}

function finalizeSection(sectionEl, validPanelIndices) {
  if (validPanelIndices.length === 0) {
    sectionEl.style.display = 'none';
    return;
  }

  const tabs = sectionEl.querySelectorAll('.recommendation-reels__tab');
  const panels = sectionEl.querySelectorAll('.recommendation-reels__panel');

  tabs.forEach((tab) => {
    if (!validPanelIndices.includes(tab.dataset.tabIndex)) {
      tab.remove();
    }
  });

  panels.forEach((panel) => {
    if (!validPanelIndices.includes(panel.dataset.panelIndex)) {
      panel.remove();
    }
  });

  const remainingTabs = sectionEl.querySelectorAll('.recommendation-reels__tab');
  const remainingPanels = sectionEl.querySelectorAll('.recommendation-reels__panel');

  remainingTabs.forEach((t) => t.classList.remove('is-active'));
  remainingPanels.forEach((p) => (p.style.display = 'none'));

  if (remainingTabs.length > 0) remainingTabs[0].classList.add('is-active');
  if (remainingPanels.length > 0) remainingPanels[0].style.display = '';

  const tabsContainer = sectionEl.querySelector('.recommendation-reels__tabs');

  if (remainingTabs.length <= 1 && tabsContainer) {
    tabsContainer.style.display = 'none';
  }
}

async function initSection(sectionEl) {
  const productId = sectionEl.dataset.productId || null;
  const variantId = sectionEl.dataset.variantId || null;
  const productHandle = sectionEl.dataset.productHandle || '';
  const objectID = variantId || productId;
  const isPDP = Boolean(objectID);
  const panels = Array.from(sectionEl.querySelectorAll('.recommendation-reels__panel'));
  const validPanelIndices = [];

  console.log('[RecommendationReels] isPDP:', isPDP, '| objectID:', objectID, '| panels:', panels.length);

  if (!isPDP) {
    panels.forEach((panel) => {
      if (MODELS_REQUIRING_OBJECT_ID.has(panel.dataset.modelType)) {
        const tab = sectionEl.querySelector(
          `.recommendation-reels__tab[data-tab-index="${panel.dataset.panelIndex}"]`
        );
        if (tab) tab.remove();
        panel.remove();
      }
    });
  }

  const remainingPanels = Array.from(sectionEl.querySelectorAll('.recommendation-reels__panel'));

  if (remainingPanels.length === 0) {
    sectionEl.style.display = 'none';
    return;
  }

  const panelConfigs = remainingPanels.map((panelEl) => ({
    panelEl,
    modelType: panelEl.dataset.modelType,
    maxProducts: parseInt(panelEl.dataset.maxProducts, 10) || 8,
  }));

  const localPanelConfigs = panelConfigs.filter(({ modelType }) => LOCAL_MODELS.has(modelType));
  const algoliaPanelConfigs = panelConfigs.filter(({ modelType }) => !LOCAL_MODELS.has(modelType));
  const recentlyViewedHandles = JSON.parse(localStorage.getItem('recentlyViewedProducts') || '[]')
    .filter((handle) => handle && handle !== productHandle);
  const panelResults = algoliaPanelConfigs.length > 0
    ? await fetchAllRecommendations(algoliaPanelConfigs, objectID)
    : [];
  const indexName = ALGOLIA_INDICES.products;

  await Promise.allSettled(
    localPanelConfigs.map(async ({ panelEl, modelType, maxProducts }) => {
      if (modelType !== 'recently_viewed') return;

      const hits = await fetchRecentlyViewedRecommendations(recentlyViewedHandles, maxProducts);
      console.log('[RecommendationReels] Hits for', modelType, ':', hits.length);

      const rendered = await renderCarousel(panelEl, hits, objectID);
      if (rendered) {
        validPanelIndices.push(panelEl.dataset.panelIndex);
      }
    })
  );

  await Promise.allSettled(
    algoliaPanelConfigs.map(async ({ panelEl, modelType }, i) => {
      const { hits, queryID } = panelResults[i] || { hits: [], queryID: null };
      console.log('[RecommendationReels] Hits for', modelType, ':', hits.length, '| queryID:', queryID);

      const rendered = await renderCarousel(panelEl, hits, objectID, { queryID, indexName });
      if (rendered) {
        validPanelIndices.push(panelEl.dataset.panelIndex);
      }
    })
  );

  finalizeSection(sectionEl, validPanelIndices);

  if (validPanelIndices.length > 0) {
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'Recommendation Viewed' });

          if (typeof window.refreshYotpoData === 'function') {
            window.refreshYotpoData();
          }
          obs.unobserve(entry.target);
        }
      });
    }, { root: null, threshold: 0.1 });

    observer.observe(sectionEl);
  }
}

function initClickAttribution(container) {
  container.addEventListener('click', (e) => {
    const card = e.target.closest('.rec-card');
    if (!card) return;

    const link = e.target.closest('a');
    if (!link) return;

    const objectID = card.dataset.insightsObjectId;
    const indexName = card.dataset.insightsIndex;
    const queryID = card.dataset.insightsQueryId;
    const position = card.dataset.insightsPosition;

    if (!objectID || !indexName) return;

    const attribution = buildAlgoliaSearchAttribution({
      objectID,
      index: indexName,
      queryID: queryID || null,
      position: position ? Number(position) : null,
    });

    if (attribution && window.setAlgoliaSearchAttribution) {
      window.setAlgoliaSearchAttribution(attribution);
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'Recommendation Clicked',
      algolia: {
        queryID: queryID || '',
        objectIDs: [objectID],
        indexName: indexName,
        positions: position ? [Number(position)] : [],
      },
    });
  });
}

export function initRecommendationReels() {
  const sections = document.querySelectorAll('.recommendation-reels');
  console.log('[RecommendationReels] Found sections:', sections.length);

  if (sections.length === 0) {
    console.warn('[RecommendationReels] No .recommendation-reels elements found in DOM');
    return;
  }

  const lazyObserver = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const sectionEl = entry.target;
        obs.unobserve(sectionEl);
        console.log('[RecommendationReels] Section entering viewport, fetching data:', sectionEl.dataset.sectionId);
        initSection(sectionEl);
      });
    },
    { rootMargin: '200px' }
  );

  sections.forEach((sectionEl) => {
    console.log('[RecommendationReels] Registering section for lazy load:', sectionEl.dataset.sectionId);
    initTabs(sectionEl);
    initSwatchDelegation(sectionEl);
    initClickAttribution(sectionEl);
    lazyObserver.observe(sectionEl);
  });
}
